import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from 'react';
import { isRoomSession, StreemError, } from '@streem/sdk-core';
import { styled, useTheme } from '@streem/ui-react';
import { RoomSessionProvider, useRoomSession } from '../context/room_session';
import { CompanySettingsProvider } from '../context/company_settings';
import { HostCallFrame } from '../components/in_call/host/host_call_frame';
import { customerRedirectUrlHandler, useObservable } from '..';
import { CustomerRedirectUrlStatuses } from '../util/redirect_helpers';
import { CompanyInfoProvider } from '../context/company_info';
const Fullscreen = styled.div(({ theme }) => `
        z-index: ${theme.layers.callFrame};
        display: flex;
        width: 100%;
    `);
export function HostInCall({ roomSession, companySettings, roomInvitationId, companyInfo, groupReservationId, 
// eslint-disable-next-line @typescript-eslint/no-empty-function
exitToLobby = () => { }, }) {
    if (!isRoomSession(roomSession)) {
        throw new StreemError('Unable to provide in-call experience without a RoomSession.');
    }
    const theme = useTheme();
    const [roomInvitationStatus] = useObservable(roomSession.roomInvitationStatus);
    const handleRedirectToCustomerRedirectUrl = (statusCode) => {
        if (companySettings?.endRedirectDisabled) {
            return;
        }
        customerRedirectUrlHandler(companySettings?.endRedirectUrl, roomSession?.roomId?.toString(), groupReservationId?.toString(), statusCode);
    };
    const onCallEndedByHost = () => {
        /* There are two possible scenarios that need to be handled when the host ends the call:
         * 1. The host has ended the call after the spectator has accepted the room invitation and the room is
         *    connected. This case should result with a status code of OK.
         * 2. The host has ended the call before the spectator has accepted the room invitation.
         *    This case should result with a status code of Canceled.
         */
        let redirectStatusCode = CustomerRedirectUrlStatuses.OK;
        /* TODO: https://streempro.atlassian.net/browse/ST-3786
         * The room invitation status is expected to be CANCELED when the host ends the call before the spectator
         * accepts the room invitation but it is currently reading as PENDING.
         */
        if (roomInvitationStatus === 'PENDING') {
            redirectStatusCode = CustomerRedirectUrlStatuses.CALL_CANCELED;
        }
        handleRedirectToCustomerRedirectUrl(redirectStatusCode);
    };
    const onRoomInvitationNotAccepted = (roomInvitationStatus) => {
        /* When using group reservation the host should be redirected to the customer redirect url
         * instead of exiting to the lobby, so they can be matched with another available spectator.
         */
        if (groupReservationId) {
            if (roomInvitationStatus === 'REJECTED') {
                handleRedirectToCustomerRedirectUrl(CustomerRedirectUrlStatuses.CALL_REJECTED);
            }
            if (roomInvitationStatus === 'TIMED_OUT') {
                handleRedirectToCustomerRedirectUrl(CustomerRedirectUrlStatuses.CALL_TIMED_OUT);
            }
        }
        else {
            exitToLobby();
        }
    };
    const onRoomDisconnected = (roomStatus) => {
        if (roomStatus === 'DISCONNECTED') {
            handleRedirectToCustomerRedirectUrl(CustomerRedirectUrlStatuses.OK);
        }
    };
    return (_jsx(Fullscreen, { theme: theme, className: "InCallFullscreen", children: _jsx(RoomSessionProvider, { value: roomSession, children: _jsx(CompanySettingsProvider, { value: companySettings, children: _jsxs(CompanyInfoProvider, { value: companyInfo, children: [_jsx(InvitationWatcher, { onRoomInvitationNotAccepted: onRoomInvitationNotAccepted, onRoomDisconnected: onRoomDisconnected, roomInvitationId: roomInvitationId }), _jsx(HostCallFrame, { onCallEndedByHost: onCallEndedByHost })] }) }) }) }));
}
const InvitationWatcher = ({ roomInvitationId, onRoomInvitationNotAccepted, onRoomDisconnected, }) => {
    const roomSession = useRoomSession();
    const [roomInvitation] = useObservable(roomSession.roomInvitation);
    const invitationIdString = roomInvitation?.id.toString();
    const roomInvitationStatus = roomInvitation?.status ?? 'UNKNOWN';
    const roomInvitationIdString = roomInvitationId?.toString();
    const [roomStatus] = useObservable(roomSession.status);
    useEffect(() => {
        // handle roomInvitation id mismatch race condition to prevent premature "REJECTED" status confirmation
        if (roomInvitation &&
            invitationIdString === roomInvitationIdString &&
            ['REJECTED', 'TIMED_OUT'].includes(roomInvitationStatus)) {
            onRoomInvitationNotAccepted(roomInvitationStatus);
        }
    }, [
        invitationIdString,
        roomInvitationIdString,
        roomInvitationStatus,
        onRoomInvitationNotAccepted,
        roomInvitation,
    ]);
    useEffect(() => {
        if (roomStatus === 'DISCONNECTED') {
            onRoomDisconnected(roomStatus);
        }
    }, [roomStatus, onRoomDisconnected]);
    return null;
};
//# sourceMappingURL=host_in_call.js.map